import { useMemo, useState } from 'react';
import type { Product } from '../lib/database.types';
import { formatPrice, type CartLine } from '../lib/cart';
import type { MenuOption } from '../lib/menu';
import {
  visibleOptions,
  lineTotalCents,
  canAddToCart,
  selectionSummary,
  buildOptionLines,
  selectedLabels,
  type Selections,
} from '../lib/menuClient';

/**
 * Fiche produit client — portée À L'IDENTIQUE du legacy (Big Burger / Food Kourt).
 * Header image grand format, titre display-font, groupes d'options en grille 2 colonnes
 * avec cartes brandées (radio/checkbox custom, badge « Requis », prix à droite, 1ʳᵉ offerte).
 * La logique reste dans menuClient.ts (testée) ; ici c'est uniquement la mise en forme prod.
 */
interface ProductModalProps {
  product: Product;
  options: MenuOption[];
  currency?: 'EUR' | 'CHF';
  onAdd: (line: CartLine) => void;
  onClose: () => void;
  /** Sélections initiales (B5 — réouverture d'une ligne du panier en édition). */
  initialSelections?: Selections;
  /** Mode édition : change le libellé du bouton en « Mettre à jour ». */
  editing?: boolean;
}

export default function ProductModal({
  product,
  options,
  currency = 'EUR',
  onAdd,
  onClose,
  initialSelections,
  editing = false,
}: ProductModalProps) {
  const [selections, setSelections] = useState<Selections>(initialSelections ?? {});

  const visible = useMemo(() => visibleOptions(options, selections), [options, selections]);
  const total = useMemo(
    () => lineTotalCents(product.base_price_cents, options, selections),
    [product.base_price_cents, options, selections],
  );
  const canAdd = useMemo(() => canAddToCart(options, selections), [options, selections]);

  const setSingle = (slug: string, label: string) =>
    setSelections((s) => ({ ...s, [slug]: label }));

  const toggleMulti = (slug: string, label: string, max: number | null) =>
    setSelections((s) => {
      const cur = selectedLabels(s[slug]);
      if (cur.includes(label)) return { ...s, [slug]: cur.filter((l) => l !== label) };
      if (max && max > 0 && cur.length >= max) return s; // max atteint
      return { ...s, [slug]: [...cur, label] };
    });

  const handleAdd = () => {
    const summary = selectionSummary(options, selections);
    onAdd({
      name: summary ? `${product.name} (${summary})` : product.name,
      unitPriceCents: total,
      quantity: 1,
      productId: product.id,
      selections,
      optionLines: buildOptionLines(options, selections),
      imageUrl: product.image_url,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 lg:items-center lg:p-6"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl lg:max-w-2xl lg:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header visuel */}
        <div
          className="relative flex h-52 flex-shrink-0 items-center justify-center text-8xl lg:h-64"
          style={
            product.image_url
              ? { background: `#f6f6f6 url(${product.image_url}) center/contain no-repeat` }
              : { background: 'var(--brand-light)' }
          }
        >
          {!product.image_url && <span>{product.emoji || '🍽️'}</span>}
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-xl shadow backdrop-blur hover:bg-white"
          >
            ✕
          </button>
          {product.is_featured && (
            <div className="absolute left-3 top-3 rounded-full bg-yellow-400 px-3 py-1.5 text-xs font-extrabold text-yellow-900 shadow">
              ★ POPULAIRE
            </div>
          )}
        </div>

        {/* Corps scrollable */}
        <div className="flex-1 overflow-y-auto p-5 lg:p-7">
          <h2 className="display-font text-3xl font-black lg:text-4xl">{product.name}</h2>
          {product.description && <p className="mt-2 text-gray-600">{product.description}</p>}
          <div className="mt-3 text-2xl font-extrabold" style={{ color: 'var(--brand-primary)' }}>
            {formatPrice(product.base_price_cents, currency)}
          </div>

          {visible.map((o) => {
            const selArr = selectedLabels(selections[o.slug]);
            const isFirstFree = o.pricingMode === 'first_free' && o.extraEachCents > 0;
            const extraCost = isFirstFree && selArr.length > 1 ? (selArr.length - 1) * o.extraEachCents : 0;
            return (
              <div key={o.slug} className="mt-6 border-t pt-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-extrabold">{o.name}</div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {o.required ? 'Obligatoire' : 'Facultatif'}
                      {o.type === 'multi' && o.max ? ` · max ${o.max}` : ''}
                    </div>
                    {isFirstFree && (
                      <div
                        className="mt-1 text-xs font-bold"
                        style={{ color: extraCost > 0 ? 'var(--brand-primary)' : '#16A34A' }}
                      >
                        {selArr.length === 0 && '🥄 1ʳᵉ offerte'}
                        {selArr.length === 1 && '✅ 1 sélectionnée — offerte'}
                        {selArr.length > 1 &&
                          `✅ ${selArr.length} choisies — 1 offerte + ${selArr.length - 1} × ${formatPrice(o.extraEachCents, currency)} = +${formatPrice(extraCost, currency)}`}
                      </div>
                    )}
                  </div>
                  {o.required && (
                    <span className="flex-shrink-0 rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700">
                      Requis
                    </span>
                  )}
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  {o.values.map((v) => {
                    const isSel =
                      o.type === 'single' ? selections[o.slug] === v.label : selArr.includes(v.label);
                    const selIdx = isFirstFree && isSel ? selArr.indexOf(v.label) : -1;
                    let priceLabel: string | null = null;
                    if (isFirstFree && isSel) {
                      priceLabel = selIdx === 0 ? 'Offerte' : `+${formatPrice(o.extraEachCents, currency)}`;
                    } else if (v.priceDeltaCents > 0) {
                      priceLabel = `+${formatPrice(v.priceDeltaCents, currency)}`;
                    }
                    return (
                      <button
                        key={v.label}
                        type="button"
                        onClick={() =>
                          o.type === 'single'
                            ? setSingle(o.slug, v.label)
                            : toggleMulti(o.slug, v.label, o.max)
                        }
                        className={
                          'flex items-center justify-between rounded-xl border-2 p-3.5 text-left transition ' +
                          (isSel ? '' : 'border-gray-200 bg-white hover:border-gray-300')
                        }
                        style={
                          isSel
                            ? { borderColor: 'var(--brand-primary)', background: 'var(--brand-light)' }
                            : {}
                        }
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <span
                            className={
                              'flex h-5 w-5 flex-shrink-0 items-center justify-center border-2 ' +
                              (o.type === 'single' ? 'rounded-full' : 'rounded')
                            }
                            style={{
                              borderColor: isSel ? 'var(--brand-primary)' : '#D1D5DB',
                              background: isSel ? 'var(--brand-primary)' : 'transparent',
                            }}
                          >
                            {isSel && (
                              <span className="text-xs leading-none text-white">
                                {o.type === 'single' ? '●' : '✓'}
                              </span>
                            )}
                          </span>
                          <span className="truncate text-sm font-semibold">{v.label}</span>
                        </span>
                        {priceLabel && (
                          <span
                            className={
                              'flex-shrink-0 text-sm font-semibold ' +
                              (selIdx === 0 ? 'text-emerald-600' : 'text-gray-700')
                            }
                          >
                            {priceLabel}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pied : ajout au panier */}
        <div className="flex-shrink-0 border-t bg-white p-4 lg:p-5">
          <button
            disabled={!canAdd}
            onClick={handleAdd}
            className="flex w-full items-center justify-between rounded-2xl px-5 py-4 font-bold text-white transition active:scale-[0.99] disabled:opacity-40"
            style={{ background: 'var(--brand-primary)' }}
          >
            <span>{editing ? 'Mettre à jour' : 'Ajouter au panier'}</span>
            <span>{formatPrice(total, currency)}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
